import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from "react";
import { useAuth } from "./auth-context";

const BASE_URL = import.meta.env.BASE_URL || "/";
function apiUrl(path: string) {
  return `${BASE_URL}api${path}`;
}

const GUEST_KEY = "nc.wishlist.guest";

function readGuest(): number[] {
  try {
    const raw = localStorage.getItem(GUEST_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    return Array.isArray(arr) ? arr.filter((n) => Number.isInteger(n) && n > 0) : [];
  } catch {
    return [];
  }
}

function writeGuest(ids: number[]) {
  try {
    localStorage.setItem(GUEST_KEY, JSON.stringify(Array.from(new Set(ids))));
  } catch {}
}

interface WishlistContextType {
  wishlistIds: number[];
  isInWishlist: (productId: number) => boolean;
  toggleWishlist: (productId: number) => Promise<void>;
  wishlistCount: number;
  loading: boolean;
}

const WishlistContext = createContext<WishlistContextType>({
  wishlistIds: [],
  isInWishlist: () => false,
  toggleWishlist: async () => {},
  wishlistCount: 0,
  loading: false,
});

export function WishlistProvider({ children }: { children: ReactNode }) {
  const { customer } = useAuth();
  const [wishlistIds, setWishlistIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);
  const mergedForCustomer = useRef<number | null>(null);

  const refreshFromServer = useCallback(async () => {
    try {
      const res = await fetch(apiUrl("/wishlist/ids"), { credentials: "include" });
      if (res.ok) {
        const ids = await res.json();
        if (Array.isArray(ids)) setWishlistIds(ids);
      }
    } catch {}
  }, []);

  // On mount / auth change:
  //  - Guest: hydrate from localStorage.
  //  - Logged in: merge any guest IDs into the server, then read back.
  useEffect(() => {
    if (!customer) {
      setWishlistIds(readGuest());
      mergedForCustomer.current = null;
      return;
    }
    const cid = (customer as any).id ?? null;
    const guestIds = readGuest();
    const doMerge = async () => {
      if (mergedForCustomer.current === cid) {
        await refreshFromServer();
        return;
      }
      mergedForCustomer.current = cid;
      if (guestIds.length > 0) {
        // Fire all POSTs in parallel; server is idempotent on duplicates.
        await Promise.allSettled(
          guestIds.map((pid) =>
            fetch(apiUrl(`/wishlist/${pid}`), { method: "POST", credentials: "include" })
          )
        );
        writeGuest([]);
      }
      await refreshFromServer();
    };
    doMerge();
  }, [customer, refreshFromServer]);

  const isInWishlist = useCallback(
    (productId: number) => wishlistIds.includes(productId),
    [wishlistIds]
  );

  const toggleWishlist = useCallback(
    async (productId: number) => {
      setLoading(true);
      try {
        const has = wishlistIds.includes(productId);
        if (!customer) {
          // Guest path: localStorage only.
          const next = has
            ? wishlistIds.filter((id) => id !== productId)
            : [...wishlistIds, productId];
          setWishlistIds(next);
          writeGuest(next);
          return;
        }
        if (has) {
          await fetch(apiUrl(`/wishlist/${productId}`), {
            method: "DELETE",
            credentials: "include",
          });
          setWishlistIds((prev) => prev.filter((id) => id !== productId));
        } else {
          await fetch(apiUrl(`/wishlist/${productId}`), {
            method: "POST",
            credentials: "include",
          });
          setWishlistIds((prev) => [...prev, productId]);
          try {
            const m = await import("./tracking");
            m.track({ eventName: "add_to_wishlist", productId });
          } catch {}
        }
      } catch {} finally {
        setLoading(false);
      }
    },
    [customer, wishlistIds]
  );

  return (
    <WishlistContext.Provider
      value={{
        wishlistIds,
        isInWishlist,
        toggleWishlist,
        wishlistCount: wishlistIds.length,
        loading,
      }}
    >
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  return useContext(WishlistContext);
}
